import { Component, Vue, Ref } from "vue-property-decorator";
import { Icon, Spin } from "ant-design-vue";
import Invitation from "@/components/invitation/invitation.vue";
import { getModule } from "vuex-module-decorators";
import User from "@/modules/user";
import { MessageHelper, NotificationType, fmtMsg } from "@/commonui/utils";
import { PermissionService } from "@/commonui/services/permission-service";
import { InvitationService } from "@/services";
import { ContentAdminLocale } from "@/locales/localeid";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { ContentAdmin } from "@/models/user-model";
import { Paths } from "@/util/paths";
import { AccessLevel, ListStates, VcsMenuCategory, VcsMenuCategoryPermission } from "@/util/enum";
import { disabledDueToAccessLevel, getRouteAccessLevel, isLinkVisible } from "@/util/func";
import ListStateRadios, {
	ListStatesRadiosProps,
	ListStatesStore
} from "@/components/list-states-radios/list-states-radios";
import { ListStateStoreKeys } from "@/util/constants";
import { ActionItem } from "commonui";

const listStateKey = "content-admin";
const defaultListState = ListStates.Active;

@Component({
	components: {
		Invitation,
		Icon,
		Spin,
		Grid,
		ActionItem
	}
})
export default class ContentAdministrator extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public userModule = getModule(User) as User;
	public contentAdmins: ContentAdmin[] = [];
	public inviteModalVisible = false;
	public loading = false;
	public listStateStore: ListStatesStore = new ListStatesStore(ListStateStoreKeys.ContentAdministrator, listStateKey);
	public activeCnt: number = 0;
	public allCnt: number = 0;
	public pendingCount: number = 0;
	public accessLevel = AccessLevel;

	mounted() {
		this.setContentAdmin();
	}

	get contentAdminLocales() {
		return ContentAdminLocale;
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	get listStateOptions(): ListStatesRadiosProps {
		return {
			value: this.listStateStore.getState(listStateKey, defaultListState),
			allCnt: this.allCnt,
			showInActiveRadio: false,
			activeCnt: this.activeCnt,
			pendingCount: this.pendingCount,
			onChange: this.onListValueChange
		};
	}

	onListValueChange(value: ListStates) {
		this.listStateStore.setState(value);
		this.setContentAdmin();
	}

	get applicationPaths() {
		return Paths;
	}

	get vcsMenuCategory() {
		return VcsMenuCategory;
	}

	get vcsMenuPermissionCategory() {
		return VcsMenuCategoryPermission;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	showAdminLink(menuId: VcsMenuCategory, permissionId?: VcsMenuCategoryPermission, superMenuId?: VcsMenuCategory) {
		return isLinkVisible(menuId, permissionId, superMenuId);
	}

	async loadContentAdminList() {
		this.setLoading(true);
		await this.userModule.getContentAdministrators();
		this.setContentAdmin();
		this.setLoading(false);
	}

	setContentAdmin() {
		const allAdmins = this.userModule.getContentAdmins;
		const activeAdmins = allAdmins.filter((u) => !u.isInvitePending);
		const pendingAdmins = allAdmins.filter((u) => u.isInvitePending);

		this.allCnt = allAdmins.length;
		this.pendingCount = pendingAdmins.length;
		this.activeCnt = activeAdmins.length;

		let contentAdminValues: ContentAdmin[] = [];
		switch (this.listStateStore.getState(listStateKey, defaultListState)) {
			case ListStates.Active:
				contentAdminValues = activeAdmins;
				break;
			case ListStates.All:
				contentAdminValues = allAdmins;
				break;
			case ListStates.Pending:
				contentAdminValues = pendingAdmins;
				break;
			default:
		}

		this.contentAdmins = contentAdminValues;
	}

	setLoading(value: boolean) {
		this.loading = value;
	}

	showRegisterModal() {
		this.inviteModalVisible = true;
	}

	unloadInvitation() {
		this.inviteModalVisible = false;
	}

	updateContentAdminList() {
		this.loadContentAdminList();
	}

	deleteContentAdmin(userId: string) {
		MessageHelper.Confirm(
			this.localizedString(this.contentAdminLocales.ContentAdminDeleteConfirmation),
			() =>
				new Promise((resolve, reject) => {
					{
						PermissionService.removepermission(userId)
							.then((data: any) => {
								MessageHelper.Message(
									NotificationType.Success,
									this.localizedString(this.contentAdminLocales.ContentAdminDeleteSucessfulMessage)
								);
								// to refresh grid
								this.loadContentAdminList();
								resolve();
							})
							.catch((ex) => {
								reject();
							});
					}
				})
		);
	}

	deleteInvitation(invitationId) {
		MessageHelper.Confirm(
			this.localizedString(this.contentAdminLocales.ContentAdminInvitationDeleteConfirmation),
			() =>
				new Promise((resolve, reject) => {
					{
						InvitationService.delete(invitationId)
							.then((data: any) => {
								MessageHelper.Message(
									NotificationType.Success,
									this.localizedString(
										this.contentAdminLocales.ContentAdminInvitationDeleteSucessfulMessage
									)
								);

								// to refresh grid
								this.loadContentAdminList();
								resolve();
							})
							.catch((ex) => {
								reject();
							});
					}
				})
		);
	}

	resendInvitation(invitationId) {
		//this.loading = true;
		InvitationService.sendEmail(invitationId, true)
			.then((data: any) => {
				//this.loading = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.localizedString(this.contentAdminLocales.ContentAdminResendSucessfulMessage)
				);
			})
			.catch((ex) => {
				//this.loading = false;
			});
	}

	editPermissions(userId: string) {
		this.userModule.setSelectedAdmin(userId);
		this.$router.push(Paths.PermissionManager);
	}
}
